import { useEffect, useRef, useState } from "react";
import "../../css/CashCollections.css";
import {
    fetchStorePayouts,
    fetchStorePayoutDetails,
    updateStorePayoutStatus,
} from "../../service/api/adminApi";

export default function StorePayouts() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [selectedRestaurantId, setSelectedRestaurantId] = useState("");
    const [status, setStatus] = useState("");
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const selectedRestaurantClickRef = useRef(false);

    const [summary, setSummary] = useState({
        overall_pending_total: 0,
        selected_restaurant_pending_total: 0,
        stores_with_pending: 0,
        search_applied: false,
    });

    const [selectedPayout, setSelectedPayout] = useState(null);
    const [payoutDetails, setPayoutDetails] = useState(null);

    const [form, setForm] = useState({
        status: "PENDING",
        transaction_mode: "",
        transaction_id: "",
        message: "",
    });

    const [meta, setMeta] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        from: 0,
        to: 0,
    });

    const loadPayouts = async () => {
        try {
            setLoading(true);

            const res = await fetchStorePayouts({
                search,
                restaurant_id: selectedRestaurantId || undefined,
                status,
                per_page: perPage,
                page,
            });

            const pagination = res?.meta || {};

            setRows(res?.data || []);
            setSummary(
                res?.summary || {
                    overall_pending_total: 0,
                    selected_restaurant_pending_total: 0,
                    stores_with_pending: 0,
                    search_applied: false,
                }
            );

            setMeta({
                current_page: pagination.current_page || page,
                last_page: pagination.last_page || 1,
                total: pagination.total || 0,
                from: pagination.from || 0,
                to: pagination.to || 0,
            });
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Failed to load store payouts");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPayouts();
    }, [search, selectedRestaurantId, status, perPage, page]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (selectedRestaurantClickRef.current) {
                selectedRestaurantClickRef.current = false;
                return;
            }

            setPage(1);
            setSelectedRestaurantId("");
            setSearch(searchInput);
        }, 600);

        return () => clearTimeout(timer);
    }, [searchInput]);
    const resetFilters = () => {
        setSearchInput("");
        setSearch("");
        setSelectedRestaurantId("");
        setStatus("");
        setPerPage(10);
        setPage(1);
    };

    const exportCsv = () => {
        const header = [
            "Store",
            "Amount",
            "Status",
            "Transaction Mode",
            "Transaction ID",
            "Message",
            "Created At",
            "Updated At",
        ];

        const body = rows.map((row) => [
            row.restaurant_name || "",
            Number(row.amount || 0).toFixed(2),
            row.status || "",
            row.transaction_mode || "",
            row.transaction_id || "",
            row.message || "",
            row.created_at || "",
            row.updated_at || "",
        ]);

        const csv = [header, ...body].map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `store-payouts-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();

        URL.revokeObjectURL(url);
    };

    const openPayoutModal = async (row) => {
        try {
            setLoading(true);

            const res = await fetchStorePayoutDetails(row.id);
            const payout = res?.data?.payout;
            const details = res?.data?.payout_details;

            setSelectedPayout(payout);
            setPayoutDetails(details);

            setForm({
                status: payout?.status || "PENDING",
                transaction_mode: payout?.transaction_mode || "",
                transaction_id: payout?.transaction_id || "",
                message: payout?.message || "",
            });
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Failed to load payout details");
        } finally {
            setLoading(false);
        }
    };
    const updatePayout = async (event) => {
        event.preventDefault();

        try {
            await updateStorePayoutStatus(selectedPayout.id, form);
            await loadPayouts();
            setSelectedPayout(null);
            setPayoutDetails(null);
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Failed to update payout");
        }
    };

    const highestPendingRestaurant =
        summary.highest_pending_restaurant || summary.highest_pending_store;
    return (
        <div className="cash-page">
            <div className="cash-head">
                <div>
                    <h2>Store Payouts</h2>
                    <p>Manage store payout logs and current pending amount after completed payments</p>
                </div>

                <button type="button" className="cash-reset-btn" onClick={resetFilters}>
                    Reset All Filters
                </button>
            </div>

            <div className="cash-card">
                <div className="cash-toolbar">
                    <input
                        className="cash-search"
                        type="text"
                        placeholder="Search restaurant name..."
                        value={searchInput}
                        onChange={(e) => {
                            selectedRestaurantClickRef.current = false;
                            setSearchInput(e.target.value);
                        }}
                    />

                    <div className="cash-toolbar-actions">
                        <select
                            className="cash-per-page"
                            value={status}
                            onChange={(e) => {
                                setStatus(e.target.value);
                                setPage(1);
                            }}
                        >
                            <option value="">All Status</option>
                            <option value="PENDING">Pending</option>
                            <option value="PROCESSING">Processing</option>
                            <option value="COMPLETED">Completed</option>
                        </select>

                        <button type="button" className="cash-export-btn" onClick={exportCsv}>
                            Export CSV
                        </button>

                        <select
                            className="cash-per-page"
                            value={perPage}
                            onChange={(e) => {
                                setPerPage(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            {[10, 25, 50, 100, 200, 500].map((value) => (
                                <option key={value} value={value}>
                                    {value}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="store-payout-mini-summary">
                    <div className="store-payout-mini-left">
                        <strong>Current Pending Summary</strong>
                        <span>Overall pending, searched store pending, and highest pending restaurant.</span>
                    </div>

                    <div className="store-payout-mini-items">
                        <div className="store-payout-mini-item">
                            <span className="store-payout-mini-label">Overall Pending</span>
                            <b className="store-payout-mini-value">
                                {"\u20b9"}{Number(summary.overall_pending_total || 0).toFixed(2)}
                            </b>
                        </div>

                        <div className="store-payout-mini-item">
                            <span className="store-payout-mini-label">Selected Store</span>
                            <b className="store-payout-mini-value">
                                {summary.search_applied
                                    ? `${"\u20b9"}${Number(
                                        summary.selected_restaurant_pending_total || 0
                                    ).toFixed(2)}`
                                    : "--"}
                            </b>
                        </div>

                        <div className="store-payout-mini-item">
                            <span className="store-payout-mini-label">Stores With Pending</span>
                            <b className="store-payout-mini-value">{summary.stores_with_pending || 0}</b>
                        </div>

                        <div className="store-payout-mini-item">
                            <span className="store-payout-mini-label">Highest Pending</span>
                            <b className="store-payout-mini-value">
                                {highestPendingRestaurant?.restaurant_name || "--"}
                            </b>
                            <small className="store-payout-mini-sub">
                                {highestPendingRestaurant
                                    ? `${"\u20b9"}${Number(
                                        highestPendingRestaurant.pending_total || 0
                                    ).toFixed(2)}`
                                    : "--"}
                            </small>
                        </div>
                    </div>
                </div>
                <div className="cash-table-wrap">
                    <table className="cash-table">
                        <thead>
                        <tr>
                            <th>Store</th>
                            <th>Paid Amount</th>
                            <th>Status</th>
                            <th>Transaction Mode</th>
                            <th>Transaction ID</th>
                            <th>Message</th>
                            <th>Created At</th>
                            <th>Updated At</th>
                            <th className="cash-center">Action</th>
                        </tr>
                        </thead>

                        <tbody>
                        {rows.map((row) => (
                            <tr key={row.id}>
                                <td>
                                    <button
                                        type="button"
                                        className="cash-name-btn"
                                        onClick={() => {
                                            selectedRestaurantClickRef.current = true;
                                            setSelectedRestaurantId(row.restaurant_id);
                                            setSearchInput(row.restaurant_name || "");
                                            setSearch(row.restaurant_name || "");
                                            setPage(1);
                                        }}
                                    >
                                        {row.restaurant_name || "--"}
                                    </button>
                                </td>

                                <td>{"\u20b9"}{Number(row.amount || 0).toFixed(2)}</td>
                                <td>{row.status || "--"}</td>
                                <td>{row.transaction_mode || "--"}</td>
                                <td>{row.transaction_id || "--"}</td>
                                <td>{row.message || "--"}</td>
                                <td>{row.created_at || "--"}</td>
                                <td>{row.updated_at || "--"}</td>

                                <td className="cash-action-cell">
                                    <button
                                        type="button"
                                        className="cash-process-btn"
                                        onClick={() => openPayoutModal(row)}
                                    >
                                        View Logs
                                    </button>
                                </td>
                            </tr>
                        ))}

                        {loading && (
                            <tr>
                                <td colSpan="9" className="cash-empty">
                                    Loading...
                                </td>
                            </tr>
                        )}

                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan="9" className="cash-empty">
                                    No data found
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                <div className="cash-pagination">
          <span className="cash-pagination-summary">
            {meta.total > 0
                ? `Showing ${meta.from || (page - 1) * perPage + 1} to ${
                    meta.to || (page - 1) * perPage + rows.length
                } of ${meta.total} entries`
                : "Showing 0 entries"}
          </span>

                    <div className="cash-page-buttons">
                        <button
                            type="button"
                            className="cash-page-btn"
                            disabled={page <= 1 || loading}
                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                        >
                            Previous
                        </button>

                        <span className="cash-page-info">
              Page {meta.current_page || page} of {meta.last_page}
            </span>

                        <button
                            type="button"
                            className="cash-page-btn"
                            disabled={loading || page >= meta.last_page}
                            onClick={() => setPage((prev) => Math.min(prev + 1, meta.last_page))}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            {selectedPayout && (
                <div
                    className="cash-modal-overlay"
                    onMouseDown={() => setSelectedPayout(null)}
                >
                    <div
                        className="cash-modal store-payout-modal"
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <div className="cash-modal-head">
                            <div>
                                <h3>
                                    {selectedPayout.restaurant_name} payout {"\u20b9"}
                                    {Number(selectedPayout.amount || 0).toFixed(2)}
                                </h3>
                                <p>Store Payout</p>
                            </div>

                            <button
                                type="button"
                                onClick={() => setSelectedPayout(null)}
                                style={{
                                    border: "none",
                                    background: "transparent",
                                    cursor: "pointer",
                                    fontSize: "22px",
                                }}
                            >
                                ❌
                            </button>
                        </div>

                        <form onSubmit={updatePayout}>
                            <div className="cash-form-row">
                                <label>Status</label>
                                <select
                                    value={form.status}
                                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                                    required
                                >
                                    <option value="PENDING">PENDING</option>
                                    <option value="PROCESSING">PROCESSING</option>
                                    <option value="COMPLETED">COMPLETED</option>
                                </select>
                            </div>

                            <div className="cash-form-row">
                                <label>Transaction Mode</label>
                                <input
                                    value={form.transaction_mode}
                                    onChange={(e) =>
                                        setForm({ ...form, transaction_mode: e.target.value })
                                    }
                                    placeholder="Transaction Mode"
                                />
                            </div>

                            <div className="cash-form-row">
                                <label>Transaction ID</label>
                                <input
                                    value={form.transaction_id}
                                    onChange={(e) =>
                                        setForm({ ...form, transaction_id: e.target.value })
                                    }
                                    placeholder="Transaction ID"
                                />
                            </div>

                            <div className="cash-form-row">
                                <label>Message</label>
                                <input
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    placeholder="Message"
                                />
                            </div>

                            <div className="store-payout-details-box">
                                <h4>Payout Account Details</h4>

                                <div className="store-payout-details-grid">
                                    <div className="store-payout-details-item">
                                        <b>Bank Name</b>
                                        {payoutDetails?.bankName || "--"}
                                    </div>

                                    <div className="store-payout-details-item">
                                        <b>Bank Code/IFSC</b>
                                        {payoutDetails?.bankCode || "--"}
                                    </div>

                                    <div className="store-payout-details-item">
                                        <b>Account Holder Name</b>
                                        {payoutDetails?.recipientName || "--"}
                                    </div>

                                    <div className="store-payout-details-item">
                                        <b>Account Number</b>
                                        {payoutDetails?.accountNumber || "--"}
                                    </div>

                                    <div className="store-payout-details-item">
                                        <b>PayPal ID</b>
                                        {payoutDetails?.paypalId || "--"}
                                    </div>

                                    <div className="store-payout-details-item">
                                        <b>UPI ID</b>
                                        {payoutDetails?.upiID || "--"}
                                    </div>
                                </div>
                            </div>

                            <div className="cash-modal-actions">


                                <button type="submit" className="cash-submit-btn">
                                    Update
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}